import { ModalBackdrop } from '../dashboard/ModalFrame';
import { useEffect, useMemo, useState } from 'react';
import { ModalCloseButton } from '../dashboard/ModalCloseButton';
import { AuthApiError, authApi, type ManagedPasskeyResult } from './api';
import {
  PasskeyCeremonyCancelledError,
  PasskeyUnavailableError,
  registerPasskey,
  supportsPasskeyLogin,
} from './passkey-browser';

interface PasskeyManagementDialogProps {
  readonly csrfToken: string;
  readonly onClose: () => void;
  readonly onReauthenticate: () => void;
}

interface PasskeyNotice {
  readonly message: string;
  readonly tone: 'neutral' | 'danger';
  readonly succeeded?: boolean;
}

const passkeyDateFormatter = new Intl.DateTimeFormat('fr-FR', {
  dateStyle: 'medium',
  timeStyle: 'short',
});

export function PasskeyManagementDialog({
  onClose,
  onReauthenticate,
}: PasskeyManagementDialogProps): React.JSX.Element {
  const [passkeys, setPasskeys] = useState<readonly ManagedPasskeyResult[]>([]);
  const [displayName, setDisplayName] = useState('');
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState(false);
  const [confirmRevokeId, setConfirmRevokeId] = useState<string | null>(null);
  const [recentAuthenticationRequired, setRecentAuthenticationRequired] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<PasskeyNotice | null>(null);
  const registrationSupported = useMemo(() => supportsPasskeyLogin(), []);

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      onClose();
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const result = await authApi.listPasskeys();
        if (!cancelled) setPasskeys(result);
      } catch {
        if (!cancelled) setError('Impossible de charger vos passkeys pour le moment.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function addPasskey(): Promise<void> {
    const requestedName = displayName.trim();
    if (requestedName.length === 0 || pending || !registrationSupported) return;

    setPending(true);
    setError(null);
    setNotice(null);
    setRecentAuthenticationRequired(false);
    try {
      const { csrfToken } = await authApi.csrfToken();
      const created = await registerPasskey(requestedName, csrfToken);
      setPasskeys((current) => [created, ...current]);
      setDisplayName('');
      setNotice({
        message: `La passkey « ${created.displayName} » a été ajoutée.`,
        tone: 'neutral',
        succeeded: true,
      });
    } catch (caught) {
      handleMutationFailure(caught, 'La passkey n’a pas pu être ajoutée.');
    } finally {
      setPending(false);
    }
  }

  async function revokePasskey(passkeyId: string): Promise<void> {
    if (pending) return;
    if (confirmRevokeId !== passkeyId) {
      setConfirmRevokeId(passkeyId);
      setError(null);
      setNotice(null);
      return;
    }

    setPending(true);
    setError(null);
    setNotice(null);
    setRecentAuthenticationRequired(false);
    try {
      const { csrfToken } = await authApi.csrfToken();
      await authApi.revokePasskey(passkeyId, csrfToken);
      const revoked = passkeys.find((passkey) => passkey.id === passkeyId);
      setPasskeys((current) => current.filter((passkey) => passkey.id !== passkeyId));
      setConfirmRevokeId(null);
      setNotice({
        message:
          revoked === undefined
            ? 'La passkey a été retirée.'
            : `La passkey « ${revoked.displayName} » a été retirée.`,
        tone: 'danger',
        succeeded: true,
      });
    } catch (caught) {
      handleMutationFailure(caught, 'La passkey n’a pas pu être retirée.');
    } finally {
      setPending(false);
    }
  }

  function handleMutationFailure(caught: unknown, fallbackMessage: string): void {
    if (caught instanceof PasskeyCeremonyCancelledError) {
      setNotice({ message: 'Ajout de la passkey annulé ou expiré.', tone: 'neutral' });
      return;
    }
    if (caught instanceof PasskeyUnavailableError) {
      setNotice({
        message: 'Ce navigateur ne permet pas d’ajouter une passkey.',
        tone: 'neutral',
      });
      return;
    }
    if (caught instanceof AuthApiError && caught.code === 'RECENT_AUTHENTICATION_REQUIRED') {
      setRecentAuthenticationRequired(true);
      return;
    }
    setError(fallbackMessage);
  }

  return (
    <ModalBackdrop
      className="tosho-modal-backdrop passkey-management-backdrop"
      onMouseDown={(event) => {
        if (event.currentTarget === event.target) onClose();
      }}
      aria-labelledby="passkey-management-title"
      close={
        <ModalCloseButton
          className="tosho-modal-close passkey-management-dialog__close"
          onClick={onClose}
          aria-label="Fermer la gestion des passkeys"
          title="Fermer"
        />
      }
    >
      <section className="tosho-modal-panel passkey-management-dialog">
        <header className="passkey-management-dialog__header tosho-modal-header">
          <div>
            <p>Sécurité du compte</p>
            <h2 id="passkey-management-title">Passkeys</h2>
          </div>
        </header>

        <div className="passkey-management-dialog__body">
          <p className="passkey-management-dialog__intro">
            Une passkey vous permet de vous connecter avec votre appareil ou une clé de sécurité,
            sans saisir votre mot de passe.
          </p>

          {recentAuthenticationRequired ? (
            <aside className="passkey-management-dialog__reauth" role="status">
              <strong>Une reconnexion est nécessaire.</strong>
              <p>Pour protéger votre compte, reconnectez-vous avant de modifier vos passkeys.</p>
              <button type="button" onClick={onReauthenticate} disabled={pending}>
                Se reconnecter
              </button>
            </aside>
          ) : null}

          {error ? (
            <p className="passkey-management-dialog__message tosho-feedback is-error" role="alert">
              {error}
            </p>
          ) : null}
          {notice ? (
            <p
              className={`passkey-management-dialog__message tosho-feedback ${notice.succeeded ? 'is-success' : 'is-info'}${
                notice.tone === 'danger' ? ' is-danger-confirmation' : ''
              }`}
              role="status"
            >
              {notice.message}
            </p>
          ) : null}

          <section
            className="passkey-management-dialog__section"
            aria-labelledby="passkey-list-title"
          >
            <div className="passkey-management-dialog__section-heading">
              <div>
                <h3 id="passkey-list-title">Vos passkeys</h3>
                <p>Retrouvez les appareils et clés de sécurité déjà enregistrés.</p>
              </div>
            </div>

            {loading ? (
              <p className="passkey-management-dialog__empty" role="status">
                Chargement…
              </p>
            ) : passkeys.length === 0 ? (
              <p className="passkey-management-dialog__empty">Aucune passkey enregistrée.</p>
            ) : (
              <ul className="passkey-management-list">
                {passkeys.map((passkey) => (
                  <li className="passkey-management-list__item" key={passkey.id}>
                    <div>
                      <strong>{passkey.displayName}</strong>
                      <span>Ajoutée le {formatPasskeyDate(passkey.createdAt)}</span>
                      <small>
                        {passkey.lastUsedAt === null
                          ? 'Jamais utilisée'
                          : `Dernière utilisation : ${formatPasskeyDate(passkey.lastUsedAt)}`}
                      </small>
                    </div>
                    <div className="passkey-management-list__actions">
                      {confirmRevokeId === passkey.id ? (
                        <>
                          <button
                            className="passkey-management-button passkey-management-button--danger"
                            type="button"
                            disabled={pending}
                            onClick={() => void revokePasskey(passkey.id)}
                          >
                            {pending ? 'Retrait…' : 'Confirmer'}
                          </button>
                          <button
                            className="passkey-management-button passkey-management-button--quiet"
                            type="button"
                            disabled={pending}
                            onClick={() => setConfirmRevokeId(null)}
                          >
                            Annuler
                          </button>
                        </>
                      ) : (
                        <button
                          className="passkey-management-button passkey-management-button--quiet"
                          type="button"
                          disabled={pending}
                          onClick={() => void revokePasskey(passkey.id)}
                        >
                          Retirer
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section
            className="passkey-management-dialog__section"
            aria-labelledby="passkey-add-title"
          >
            <div className="passkey-management-dialog__section-heading">
              <div>
                <h3 id="passkey-add-title">Ajouter une passkey</h3>
                <p>Donnez-lui un nom qui vous permettra de la reconnaître facilement.</p>
              </div>
            </div>

            {registrationSupported ? (
              <div className="passkey-management-add">
                <label>
                  <span>Nom de la passkey</span>
                  <input
                    type="text"
                    maxLength={80}
                    autoComplete="off"
                    value={displayName}
                    placeholder="MacBook Pro, iPhone, clé USB…"
                    onChange={(event) => setDisplayName(event.currentTarget.value)}
                    disabled={pending || recentAuthenticationRequired}
                  />
                </label>
                <button
                  className="passkey-management-button passkey-management-button--primary"
                  type="button"
                  onClick={() => void addPasskey()}
                  disabled={
                    pending || recentAuthenticationRequired || displayName.trim().length === 0
                  }
                >
                  {pending ? 'Ajout…' : 'Ajouter une passkey'}
                </button>
              </div>
            ) : (
              <p className="passkey-management-dialog__empty">
                Ce navigateur ne permet pas d’ajouter une passkey. Vous pouvez toujours retirer
                celles déjà enregistrées.
              </p>
            )}
          </section>
        </div>
      </section>
    </ModalBackdrop>
  );
}

function formatPasskeyDate(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'date inconnue' : passkeyDateFormatter.format(date);
}
